import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { reportError } from "../lib/errorReporter";
import { apiFieldErrors } from "../lib/companyValidation";
import { fmtDate, fmtDateTime } from "../lib/format";
import {
  LICENSE_STATUSES,
  MAX_INTERNAL_NOTE,
  platformErrorMessage,
  validUntilToInput,
  validateInternalNote,
  validateSeatLimit,
} from "../lib/platformValidation";
import PlatformShell from "../components/platform/PlatformShell";
import PlatformSeatField from "../components/platform/PlatformSeatField";
import PlatformAuditHistory from "../components/platform/PlatformAuditHistory";
import PlatformLifecycleCard from "../components/platform/PlatformLifecycleCard";
import PlatformLifecycleActions from "../components/platform/PlatformLifecycleActions";
import PlatformOrgProfileForm from "../components/platform/PlatformOrgProfileForm";
import PlatformOwnerCard from "../components/platform/PlatformOwnerCard";
import PlatformInvitationHistory from "../components/platform/PlatformInvitationHistory";
import CompanyField from "../components/company/CompanyField";

const draftFrom = (snapshot) => ({
  status: snapshot?.license?.status || "active",
  valid_until: validUntilToInput(snapshot?.license?.valid_until),
  internal_note: snapshot?.license?.internal_note || "",
  manager_seat_limit: String(snapshot?.seats?.manager?.limit ?? 0),
  sales_seat_limit: String(snapshot?.seats?.sales?.limit ?? 0),
});

/**
 * Platform console — one organization: lifecycle, identity, Owner, license and
 * role seat limits.
 *
 * The owner seat is displayed read-only and locked to 1. Manager and sales seats
 * are editable, always shown next to their current active usage. Client-side
 * checks are convenience only: the backend re-validates every value, refuses a
 * limit below active usage, forces `source = manual`, records who changed it and
 * writes exactly one platform audit entry — atomically.
 *
 * Every lifecycle ACTION (archive, restore, request deletion, cancel, purge)
 * lives in `PlatformLifecycleActions`, directly under the read-only lifecycle
 * card. A successful purge destroys the organization, so the screen it was
 * started from cannot survive it: the console returns to the list with a
 * Bulgarian confirmation.
 */
export default function PlatformAdminOrganizationDetail() {
  const { t } = useTranslation();
  const { organizationId } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const [flash, setFlash] = useState(() => location.state?.flash || null);
  const [snapshot, setSnapshot] = useState(null);
  const [draft, setDraft] = useState(draftFrom(null));
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);

  const apply = useCallback((data) => {
    setSnapshot(data);
    setDraft(draftFrom(data));
  }, []);

  const load = useCallback(async () => {
    setFormError("");
    try {
      const r = await api.get(`/platform-admin/organizations/${organizationId}`, {
        skipOrgHeader: true,
      });
      apply(r.data);
    } catch (err) {
      reportError("platform.organizationDetail", err);
      if (err?.response?.status === 404) setNotFound(true);
      else setFormError(platformErrorMessage(err, t("common.error_generic")));
    }
  }, [apply, organizationId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const baseline = useMemo(() => draftFrom(snapshot), [snapshot]);
  const seats = snapshot?.seats || {};

  const changed = useMemo(() => {
    if (!snapshot) return {};
    const out = {};
    if (draft.status !== baseline.status) out.status = draft.status;
    if (draft.valid_until !== baseline.valid_until) {
      out.valid_until = draft.valid_until || null;
    }
    if (draft.internal_note.trim() !== baseline.internal_note) {
      out.internal_note = draft.internal_note.trim() || null;
    }
    if (draft.manager_seat_limit !== baseline.manager_seat_limit) {
      out.manager_seat_limit = Number(draft.manager_seat_limit);
    }
    if (draft.sales_seat_limit !== baseline.sales_seat_limit) {
      out.sales_seat_limit = Number(draft.sales_seat_limit);
    }
    return out;
  }, [baseline, draft, snapshot]);

  const dirty = Object.keys(changed).length > 0;

  const setField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    setNotice("");
  };

  const validate = () => {
    const found = {};
    const manager = validateSeatLimit(
      "manager",
      draft.manager_seat_limit,
      seats.manager?.usage,
    );
    if (manager) found.manager_seat_limit = manager;
    const sales = validateSeatLimit("sales", draft.sales_seat_limit, seats.sales?.usage);
    if (sales) found.sales_seat_limit = sales;
    const note = validateInternalNote(draft.internal_note);
    if (note) found.internal_note = note;
    if (!LICENSE_STATUSES.includes(draft.status)) found.status = t("platform.invalid_status");
    return found;
  };

  const save = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    setNotice("");
    setFormError("");
    const found = validate();
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    setErrors({});
    if (!dirty) {
      setNotice(t("platform.no_changes"));
      return;
    }
    inFlight.current = true;
    setSaving(true);
    try {
      const r = await api.patch(
        `/platform-admin/organizations/${organizationId}/license`,
        changed,
        { skipOrgHeader: true },
      );
      apply(r.data);
      setNotice(r.data?.changed ? t("platform.saved") : t("platform.no_changes"));
    } catch (err) {
      reportError("platform.saveLicense", err);
      const fields = apiFieldErrors(err);
      if (fields) setErrors(fields);
      else setFormError(platformErrorMessage(err, t("common.error_generic")));
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  if (notFound) {
    return (
      <PlatformShell title={t("platform.organizations")}>
        <div className="qw-card qw-empty" data-testid="platform-org-not-found">
          {t("platform.not_found")}
        </div>
      </PlatformShell>
    );
  }

  if (!snapshot) {
    return (
      <PlatformShell title={t("platform.organizations")}>
        <div className="qw-empty" data-testid="platform-detail-loading">
          {t("common.loading")}
        </div>
        {formError && (
          <div className="qw-error" data-testid="platform-detail-error">
            {formError}
          </div>
        )}
      </PlatformShell>
    );
  }

  const license = snapshot.license || {};

  return (
    <PlatformShell
      title={snapshot.organization?.name || t("platform.organizations")}
      actions={
        <Link to="/platform-admin" className="qw-btn ghost sm" data-testid="platform-back-to-list">
          {t("platform.back_to_list")}
        </Link>
      }
    >
      {flash && (
        <div
          className={flash.tone === "warning" ? "qw-error" : "qw-help"}
          style={flash.tone === "warning" ? { marginBottom: 12 } : { color: "#059669", marginBottom: 12 }}
          data-testid="platform-detail-flash"
        >
          {flash.text}
          <button
            type="button"
            className="qw-btn ghost sm"
            style={{ marginLeft: 8 }}
            onClick={() => setFlash(null)}
            data-testid="platform-detail-flash-dismiss"
          >
            {t("platform.dismiss")}
          </button>
        </div>
      )}

      <PlatformLifecycleCard
        organization={snapshot.organization}
        lifecycle={snapshot.lifecycle}
      />
      <div style={{ height: 16 }} />
      <PlatformLifecycleActions
        snapshot={snapshot}
        organizationId={organizationId}
        onSnapshot={apply}
        onPurged={() =>
          nav("/platform-admin", {
            state: { flash: { tone: "success", text: t("platform.purged") } },
          })
        }
      />
      <div style={{ height: 16 }} />
      <PlatformOwnerCard
        snapshot={snapshot}
        organizationId={organizationId}
        onSnapshot={apply}
      />
      <div style={{ height: 16 }} />
      <PlatformOrgProfileForm
        snapshot={snapshot}
        organizationId={organizationId}
        onSnapshot={apply}
      />
      <div style={{ height: 16 }} />

      <form onSubmit={save} noValidate data-testid="platform-license-form">
        <div className="qw-card">
          <div style={{ fontWeight: 600, marginBottom: 12 }}>{t("platform.license_section")}</div>
          <div className="qw-grid cols-2">
            <div className="qw-field">
              <label htmlFor="platform-license-status">{t("platform.status_label")}</label>
              <select
                id="platform-license-status"
                className="qw-select"
                value={draft.status}
                onChange={(e) => setField("status", e.target.value)}
                aria-invalid={errors.status ? "true" : undefined}
                data-testid="platform-license-status"
              >
                {LICENSE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`platform.status_${status}`)}
                  </option>
                ))}
              </select>
              <div className="qw-help">{t("platform.status_help")}</div>
              {errors.status && (
                <div className="qw-error" data-testid="platform-license-status-error">
                  {errors.status}
                </div>
              )}
            </div>

            <div className="qw-field">
              <label>{t("platform.valid_from")}</label>
              <div className="qw-platform-readonly" data-testid="platform-license-valid-from">
                {fmtDate(license.valid_from)}
              </div>
            </div>

            <CompanyField
              id="platform-license-valid-until"
              testId="platform-license-valid-until"
              label={t("platform.valid_until")}
              type="date"
              value={draft.valid_until}
              onChange={(v) => setField("valid_until", v)}
              error={errors.valid_until}
              help={t("platform.valid_until_help")}
            />

            <div className="qw-field">
              <label>{t("platform.source")}</label>
              <div className="qw-platform-readonly" data-testid="platform-license-source">
                {license.source ? t(`platform.source_${license.source}`) : "—"}
              </div>
              <div className="qw-help" data-testid="platform-license-updated-at">
                {t("platform.updated_at")}: {fmtDateTime(license.updated_at)}
              </div>
            </div>
          </div>

          <CompanyField
            id="platform-license-note"
            testId="platform-license-note"
            label={t("platform.internal_note")}
            value={draft.internal_note}
            onChange={(v) => setField("internal_note", v)}
            error={errors.internal_note}
            help={t("platform.internal_note_help")}
            multiline
            rows={3}
            maxLength={MAX_INTERNAL_NOTE}
          />
        </div>

        <div style={{ height: 16 }} />
        <div className="qw-card" data-testid="platform-seats-section">
          <div style={{ fontWeight: 600, marginBottom: 12 }}>{t("platform.seats_section")}</div>
          <div className="qw-help" data-testid="platform-seats-usage-hint">
            {t("platform.occupied_seats_hint")}
          </div>
          <div className="qw-grid cols-3" style={{ marginTop: 10 }}>
            <PlatformSeatField
              id="platform-seat-owner"
              testId="platform-seat-owner"
              label={t("platform.owner_seats")}
              value={String(seats.owner?.limit ?? 1)}
              usage={seats.owner?.usage ?? 0}
              usageLabel={t("platform.occupied_seats")}
              overLimit={Boolean(seats.owner?.over_limit)}
              editable={false}
              lockedHint={t("platform.owner_seats_locked")}
            />
            <PlatformSeatField
              id="platform-seat-manager"
              testId="platform-seat-manager"
              label={t("platform.manager_seats")}
              value={draft.manager_seat_limit}
              usage={seats.manager?.usage ?? 0}
              usageLabel={t("platform.occupied_seats")}
              overLimit={Boolean(seats.manager?.over_limit)}
              editable
              disabled={saving}
              help={t("platform.manager_seats_help")}
              error={errors.manager_seat_limit}
              onChange={(v) => setField("manager_seat_limit", v)}
            />
            <PlatformSeatField
              id="platform-seat-sales"
              testId="platform-seat-sales"
              label={t("platform.sales_seats")}
              value={draft.sales_seat_limit}
              usage={seats.sales?.usage ?? 0}
              usageLabel={t("platform.occupied_seats")}
              overLimit={Boolean(seats.sales?.over_limit)}
              editable
              disabled={saving}
              help={t("platform.sales_seats_help")}
              error={errors.sales_seat_limit}
              onChange={(v) => setField("sales_seat_limit", v)}
            />
          </div>
        </div>

        {formError && (
          <div className="qw-error" style={{ marginTop: 12 }} data-testid="platform-license-error">
            {formError}
          </div>
        )}
        {notice && (
          <div
            className="qw-help"
            style={{ color: "#059669", marginTop: 12 }}
            data-testid="platform-license-saved"
          >
            {notice}
          </div>
        )}
        {dirty && (
          <div className="qw-help" style={{ marginTop: 8 }} data-testid="platform-license-unsaved">
            {t("platform.unsaved")}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button
            type="button"
            className="qw-btn ghost"
            onClick={() => {
              setDraft(baseline);
              setErrors({});
              setNotice("");
              setFormError("");
            }}
            disabled={saving || !dirty}
            data-testid="platform-license-cancel"
          >
            {t("platform.cancel")}
          </button>
          <button
            type="submit"
            className="qw-btn primary"
            disabled={saving}
            data-testid="platform-license-save"
          >
            {saving ? t("platform.saving") : t("platform.save")}
          </button>
        </div>
      </form>

      <div style={{ height: 16 }} />
      <PlatformInvitationHistory entries={snapshot.invitations} />
      <div style={{ height: 16 }} />
      <PlatformAuditHistory entries={snapshot.audit} />
    </PlatformShell>
  );
}
